/**
 * Qty steppers for PLP, Quick Lists, Frequently Purchased, compare, and quote.
 * Steps by min / multiple. Quote rows also sync Vue so the email uses the same qty.
 *
 * Frequently Purchased now receives pre-rendered .qnt-count wrappers from the backend
 * with data-min-order, data-multiple-qty on the wrapper and step="" on the input.
 * We still need to bind click handlers to those backend-rendered buttons.
 */
(() => {
  const readyClass = 'js-qty-ready';
  const boundClass = 'js-qty-bound';
  const clickBoundClass = 'js-click-bound';
  const erpCacheKey = 'oemeyer-erp-qty';
  const qtyWrapSelector = [
    '.tile .qnt-count',
    '.tileparent .qnt-count',
    '#productCompare .qnt-count',
    '#productCompareModal .qnt-count',
  ].join(', ');

  // Skip rows already wrapped when pricing / inventory / load-more fire again
  const qtySelector = [
    `.tile:not(.${readyClass}) .buttons.group > input.inputquantity`,
    `.ex-quicklists .tileparent:not(.${readyClass}) .buttons.group > input.inputquantity`,
    `.ex-frequentlypurchased .tileparent:not(.${readyClass}) .buttons.group > input.inputquantity`,
  ].join(', ');
  const quoteQtySelector = [
    `#quote-product-list .qnt-count:not(.${readyClass})`,
    `.table-fixed .qnt-count:not(.${readyClass})`,
  ].join(', ');
  const compareQtySelector = '#productCompareModal .buttons.group > input.inputquantity';

  // Pre-rendered FP wrappers from backend — already have structure but need click handlers
  const preRenderedFpSelector = `.ex-frequentlypurchased .qnt-count.${boundClass}:not(.${clickBoundClass})`;

  const markQtyReady = (el) => {
    el.classList.add(readyClass);
    el.closest('.buttons.group')?.classList.add(readyClass);
    const host = el.closest('.tile') || el.closest('.tileparent') || el.closest('tr');
    host?.classList.add(readyClass);
  };

  const markClickBound = (el) => {
    el.classList.add(clickBoundClass);
  };

  const findItemClass = (el) => {
    const host = el.closest('[class*="item_"]');
    return host ? [...host.classList].find((cls) => /^item_\d+$/.test(cls)) || '' : '';
  };

  const isQuoteQty = (el) => !!(
    el.closest('#quote-product-list')
    || el.closest('#quote-product-list-modal')
  );

  // FP starts at 0 and allows decrease to 0 — batch add only includes qty > 0
  const allowsZeroQty = (el) => !!el.closest('.ex-frequentlypurchased');

  const parseQty = (value, fallback = NaN) => {
    const num = parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(num) ? Math.round(num * 1000) / 1000 : fallback;
  };

  const formatQty = (value) => String(parseQty(value, 0));

  const readMeta = (text, keys) => {
    if (!text) return null;
    const found = keys
      .map((key) => text.match(new RegExp(`["']?${key}["']?\\s*[:=]\\s*["']?(\\d+(?:[.,]\\d+)?)`)))
      .find((match) => match);
    return found ? found[1].replace(',', '.') : null;
  };

  const getMin = (wrapper) => {
    const dataMin = parseQty(wrapper.dataset.minOrder, 1) || 1;

    // If step is provided and greater than dataMin, use step as minimum
    // (if you step by 10, first valid qty is 10, not 1)
    const input = wrapper.querySelector('input');
    const inputStep = parseQty(input?.step, 0);
    if (inputStep > 0 && inputStep > dataMin) return inputStep;

    return dataMin;
  };

  const getStep = (wrapper) => {
    // First check multipleQty data attribute
    const multiple = parseQty(wrapper.dataset.multipleQty, 0);
    if (multiple > 0) return multiple;

    // Then check input's step attribute (backend sets this on FP)
    const input = wrapper.querySelector('input');
    const inputStep = parseQty(input?.step, 0);
    if (inputStep > 0) return inputStep;

    // Fallback to min
    return getMin(wrapper);
  };

  const nextQty = (val, step, min, dir, floor = min) => {
    const next = parseQty(val + dir * step);
    if (dir > 0 && next < min) return min;
    return next < floor ? floor : next;
  };

  const itemKeys = (input) => {
    const itemClass = findItemClass(input);
    const id = itemClass ? itemClass.replace(/^item_/, '') : '';
    const tile = input.closest('.tileparent') || input.closest('.tile') || input.closest('.product');
    const sku = tile?.dataset.sku
      || tile?.querySelector('[data-sku]')?.dataset.sku
      || '';
    return {
      itemClass,
      id,
      sku,
      tile,
    };
  };

  // Vue 2 stores the instance on the DOM node
  // eslint-disable-next-line no-underscore-dangle
  const getVueInst = (el) => (el ? el.__vue__ : null);

  const findQuoteVue = (start) => {
    let el = start;
    while (el) {
      const vue = getVueInst(el);
      if (vue?.updateQty || vue?.quote?.items) return vue;
      el = el.parentElement;
    }
    return null;
  };

  const findQuoteProduct = (input, vue) => {
    const items = vue?.quote?.items;
    if (!items?.length) return null;

    const row = input.closest('tr');
    const index = row ? [...row.parentElement.children].indexOf(row) : -1;
    const code = row?.querySelector('[name="code"]')?.textContent?.replace(/Item Code/i, '').trim();
    return items[index] || items.find((item) => {
      const sku = String(item.sku || item.item_code || '').trim();
      return sku && code && (sku === code || code.includes(sku) || sku.includes(code));
    }) || null;
  };

  // Quote email reads Vue product.quantity, not only the input
  const syncQuoteModel = (input, value) => {
    const vue = findQuoteVue(input);
    const product = findQuoteProduct(input, vue);
    if (!vue || !product) return;

    const qty = parseQty(value, product.quantity);
    vue.updateQty?.(product, { target: { value: formatQty(qty) } });
    if (vue.$set) vue.$set(product, 'quantity', qty);
    else product.quantity = qty;
  };

  // Sync qty value across all inputs for the same SKU (PLP, FP, compare modal)
  const syncMatchingInputs = (input, value) => {
    const { itemClass, sku, tile } = itemKeys(input);
    if (!itemClass && !sku) return;

    const selectors = [];
    if (itemClass) selectors.push(`.${itemClass} .qnt-count input`);
    if (sku) selectors.push(`[data-sku="${sku}"] .qnt-count input`);

    // FP uses data-pid for compare modal matching (modal uses item_{data-pid})
    const pid = tile?.dataset.pid;
    if (pid) selectors.push(`.item_${pid} .qnt-count input`);

    const formatted = formatQty(value);
    document.querySelectorAll(selectors.join(', ')).forEach((el) => {
      if (el === input || el.value === formatted) return;
      el.value = formatted;
      // Sync minus button state
      const wrapper = el.closest('.qnt-count');
      const minus = wrapper?.querySelector('.incr-btn, button');
      if (minus) {
        const min = getMin(wrapper);
        const atMin = parseQty(formatted, min) <= min;
        minus.classList.toggle('disabled', atMin);
        minus.disabled = atMin;
      }
    });
  };

  // When compare modal opens, sync its inputs from FP page inputs
  // Only applies to FP page where item_id vs data-pid mismatch exists
  const syncCompareFromPage = () => {
    // Only sync on FP page
    if (!document.querySelector('.ex-frequentlypurchased')) return;

    document.querySelectorAll('#productCompareModal .qnt-count input').forEach((modalInput) => {
      const { itemClass } = itemKeys(modalInput);
      // Modal uses item_{data-pid}, extract the ID
      const modalPid = itemClass ? itemClass.replace('item_', '') : '';
      if (!modalPid) return;

      // Find FP page input where data-pid matches the modal's item class ID
      const fpTile = document.querySelector(`.ex-frequentlypurchased [data-pid="${modalPid}"]`);
      if (!fpTile) return;

      const pageInput = fpTile.querySelector('.qnt-count input');
      if (!pageInput) return;

      const pageVal = parseQty(pageInput.value, 0);
      if (pageVal > 0 && modalInput.value !== formatQty(pageVal)) {
        modalInput.value = formatQty(pageVal);
        // Sync minus button state
        const wrapper = modalInput.closest('.qnt-count');
        const minus = wrapper?.querySelector('.incr-btn, button');
        if (minus) {
          const min = getMin(wrapper);
          const atMin = pageVal <= min;
          minus.classList.toggle('disabled', atMin);
          minus.disabled = atMin;
        }
      }
    });
  };

  const erpQtyCache = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(erpCacheKey)) || {};
    } catch (_err) {
      return {};
    }
  })();

  const cacheErpQty = (id, sku, meta) => {
    const min = parseQty(meta.minOrder, 0);
    if (min <= 0) return;
    const next = {
      minOrder: formatQty(min),
      multipleQty: formatQty(meta.multipleQty || 0),
      isMultiple: String(meta.isMultiple || '0'),
    };
    [id, sku].filter(Boolean).forEach((key) => {
      const prevMin = parseQty(erpQtyCache[key]?.minOrder, 0);
      if (!erpQtyCache[key] || min >= prevMin) erpQtyCache[key] = next;
    });
    try {
      sessionStorage.setItem(erpCacheKey, JSON.stringify(erpQtyCache));
    } catch (_err) {
      // sessionStorage full — in-memory cache still works
    }
  };

  const readCachedQty = (input) => {
    const { id, sku } = itemKeys(input);
    return erpQtyCache[id] || erpQtyCache[sku] || null;
  };

  const rememberMeta = (input, meta) => {
    const { id, sku } = itemKeys(input);
    cacheErpQty(id, sku, meta);
  };

  // Walk a live-pricing JSON payload and store any min we find
  const ingestErpPayload = (payload) => {
    const walk = (node, keyHint = '') => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        node.forEach((item) => walk(item));
        return;
      }
      const min = parseQty(
        node.item_minimum_order
          || node.minimum_order
          || node.min_order
          || node.min_qty
          || node.minimum_qty
          || node.minqty,
        0,
      );
      const hintedId = /^\d+$/.test(keyHint) ? keyHint : '';
      const id = node.item_id || node.itemId || node.item || hintedId;
      const sku = node.item_sku || node.sku || node.item_code || '';
      if (min > 0 && (id || sku)) {
        cacheErpQty(String(id || ''), String(sku || ''), {
          minOrder: min,
          multipleQty: node.item_multiple_qty || node.multiple_qty || 0,
          isMultiple: node.item_is_multiple || node.is_multiple || '0',
        });
      }
      Object.keys(node).forEach((key) => walk(node[key], key));
    };
    walk(payload);
  };

  const readLiveItem = (input) => {
    const { id } = itemKeys(input);
    const obj = id ? window[`obj_${id}`] : null;
    return obj && typeof obj === 'object' ? obj : null;
  };

  const readJqMin = (el) => {
    if (!el || typeof window.jQuery !== 'function') return 0;
    return parseQty(window.jQuery(el).data('min-order'), 0);
  };

  const readLiveMin = (input, tile) => {
    const notice = tile?.querySelector('.min-order strong') || tile?.querySelector('.min-order');
    const liveObj = readLiveItem(input);
    const noticeMin = parseQty(notice?.textContent, 0);
    const attrMin = parseQty(input.min, 0);
    const objMin = parseQty(
      liveObj?.item_minimum_order || liveObj?.minimum_order || liveObj?.min_qty,
      0,
    );
    const jqMin = Math.max(
      readJqMin(input.closest('.qnt-count')),
      readJqMin(tile),
    );
    return Math.max(noticeMin || 0, attrMin > 0 ? attrMin : 0, objMin || 0, jqMin || 0);
  };

  // Same SKU on compare / PLP — CMS writes the ERP min on those wrappers
  const findOtherPlpWrap = (input) => {
    const selfWrap = input.closest('.qnt-count');
    const { itemClass, sku } = itemKeys(input);
    const nodes = [];
    if (itemClass) {
      document.querySelectorAll(`.${itemClass} .qnt-count`).forEach((el) => nodes.push(el));
    }
    if (sku) {
      document.querySelectorAll(`[data-sku="${sku}"] .qnt-count`).forEach((el) => nodes.push(el));
    }
    return nodes.reduce((best, el) => {
      if (el === selfWrap) return best;
      const n = parseQty(el.dataset.minOrder, 0);
      if (!n) return best;
      if (!best || n > parseQty(best.dataset.minOrder, 0)) return el;
      return best;
    }, null);
  };

  const parseQtyMeta = (input) => {
    const group = input.closest('.buttons.group');
    const { tile } = itemKeys(input);
    const row = input.closest('tr');
    const onclick = tile?.querySelector('[onclick*="minimum_order"], [onclick*="multiple_qty"]')
      ?.getAttribute('onclick') || '';
    const script = group?.querySelector('script')?.textContent
      || row?.querySelector('script')?.textContent
      || '';
    const blob = `${onclick}\n${script}`;
    const liveObj = readLiveItem(input);
    const selfWrap = input.closest('.qnt-count');
    const otherWrap = findOtherPlpWrap(input);
    const cached = readCachedQty(input);

    const parsedMin = parseQty(readMeta(blob, ['item_minimum_order']), 0);
    const liveMin = readLiveMin(input, tile);
    const selfMin = parseQty(selfWrap?.dataset.minOrder, 0);
    const otherMin = parseQty(otherWrap?.dataset.minOrder, 0);
    const cacheMin = parseQty(cached?.minOrder, 0);
    const attrMin = parseQty(input.min, 0);
    // Compare / PLP input value is often the ERP min. FP stays 0 / catalog.
    const inputVal = allowsZeroQty(input) ? 0 : parseQty(input.value, 0);

    const parsedMult = cached?.multipleQty
      || readMeta(blob, ['item_multiple_qty'])
      || liveObj?.item_multiple_qty
      || liveObj?.multiple_qty
      || otherWrap?.dataset.multipleQty
      || input.dataset.multipleqty
      || '0';
    const parsedIsMult = cached?.isMultiple
      || readMeta(blob, ['item_is_multiple'])
      || liveObj?.item_is_multiple
      || otherWrap?.dataset.isMultiple
      || '0';

    // Quote input.min is often 1 (wrong) — never treat the current qty as the min
    if (isQuoteQty(input)) {
      const vue = findQuoteVue(input);
      const product = findQuoteProduct(input, vue);
      const vueMin = parseQty(
        product?.item_minimum_order
          || product?.minimum_order
          || product?.min_qty
          || product?.minimum_qty,
        0,
      );
      const vueMult = product?.item_multiple_qty || product?.multiple_qty;
      return {
        minOrder: formatQty(vueMin || cacheMin || parsedMin || (attrMin > 1 ? attrMin : 0) || 1),
        multipleQty: String(vueMult || parsedMult),
        isMultiple: String(parsedIsMult),
      };
    }

    const erpMin = Math.max(liveMin, otherMin, cacheMin, inputVal, selfMin, 0);
    return {
      minOrder: formatQty(erpMin || parsedMin || 1),
      multipleQty: String(parsedMult),
      isMultiple: String(parsedIsMult),
    };
  };

  const bindQtyControls = (wrapper, input, decreaseBtn, increaseBtn, { clicks = true } = {}) => {
    if (wrapper.classList.contains(boundClass) || wrapper.classList.contains(readyClass)) return;
    wrapper.classList.add(boundClass);

    const meta = parseQtyMeta(input);
    Object.assign(wrapper.dataset, meta);
    rememberMeta(input, meta);

    let writing = false;

    const setQty = (value, silent = false) => {
      writing = true;
      const next = formatQty(value);
      input.value = next;
      syncQuoteModel(input, next);
      syncMatchingInputs(input, next);
      if (!silent && !isQuoteQty(input)) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      }
      writing = false;
    };

    const syncMinus = () => {
      if (!decreaseBtn) return;
      const floor = allowsZeroQty(input) ? 0 : getMin(wrapper);
      const atMin = parseQty(input.value, floor) <= floor;
      decreaseBtn.classList.toggle('disabled', atMin);
      decreaseBtn.disabled = atMin;
    };

    const stepBy = (dir) => {
      const min = getMin(wrapper);
      const floor = allowsZeroQty(input) ? 0 : min;
      setQty(nextQty(parseQty(input.value, floor), getStep(wrapper), min, dir, floor));
      syncMinus();
    };

    const current = parseQty(input.value, 0);
    const isFp = allowsZeroQty(input);
    // FP stays at 0 on load; other pages snap to min
    if (!isFp && (!current || current < getMin(wrapper))) {
      setQty(getMin(wrapper), false);
    }

    // Quote clicks are delegated — Vue replaces these buttons on each update
    if (clicks) {
      const onStep = (dir) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        stepBy(dir);
      };

      decreaseBtn.addEventListener('click', onStep(-1), true);
      increaseBtn.addEventListener('click', onStep(1), true);
    }

    input.addEventListener('change', () => {
      if (writing) {
        syncMinus();
        return;
      }
      const min = getMin(wrapper);
      const val = parseQty(input.value, NaN);
      if (allowsZeroQty(input) && (!Number.isFinite(val) || val <= 0)) {
        setQty(0);
      } else if (!val || val < min) {
        setQty(min);
      } else {
        setQty(nextQty(val - getStep(wrapper), getStep(wrapper), min, 1));
      }
      syncMinus();
    });

    syncMinus();
    markQtyReady(wrapper);
  };

  /**
   * Bind click handlers to pre-rendered FP wrappers from the backend.
   * The backend now renders the .qnt-count wrapper with buttons and data attributes,
   * but we still need to attach the click handlers.
   */
  const bindPreRenderedFpWrapper = (wrapper) => {
    if (wrapper.classList.contains(clickBoundClass)) return;

    const input = wrapper.querySelector('input.inputquantity, input[type="number"]');
    const buttons = wrapper.querySelectorAll('.incr-btn, button');
    if (!input || buttons.length < 2) return;

    const decreaseBtn = buttons[0];
    const increaseBtn = buttons[1];

    let writing = false;

    const setQty = (value, silent = false) => {
      writing = true;
      const next = formatQty(value);
      input.value = next;
      syncMatchingInputs(input, next);
      if (!silent) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      }
      writing = false;
    };

    const syncMinus = () => {
      const floor = allowsZeroQty(input) ? 0 : getMin(wrapper);
      const atMin = parseQty(input.value, floor) <= floor;
      decreaseBtn.classList.toggle('disabled', atMin);
      decreaseBtn.disabled = atMin;
    };

    const stepBy = (dir) => {
      const min = getMin(wrapper);
      const floor = allowsZeroQty(input) ? 0 : min;
      setQty(nextQty(parseQty(input.value, floor), getStep(wrapper), min, dir, floor));
      syncMinus();
    };

    const onStep = (dir) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      stepBy(dir);
    };

    decreaseBtn.addEventListener('click', onStep(-1), true);
    increaseBtn.addEventListener('click', onStep(1), true);

    input.addEventListener('change', () => {
      if (writing) {
        syncMinus();
        return;
      }
      const min = getMin(wrapper);
      const val = parseQty(input.value, NaN);
      if (allowsZeroQty(input) && (!Number.isFinite(val) || val <= 0)) {
        setQty(0, true);
      } else if (!val || val < min) {
        setQty(min, true);
      } else {
        setQty(nextQty(val - getStep(wrapper), getStep(wrapper), min, 1), true);
      }
      syncMinus();
    });

    syncMinus();
    markClickBound(wrapper);
  };

  const enhancePreRenderedFpWrappers = () => {
    document.querySelectorAll(preRenderedFpSelector).forEach(bindPreRenderedFpWrapper);
  };

  const makeBtn = (label) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'incr-btn';
    btn.textContent = label;
    btn.setAttribute('aria-label', label === '-' ? 'Decrease quantity' : 'Increase quantity');
    return btn;
  };

  // Quick Lists: [stepper | cart] [delete | price]
  // Frequently Purchased: [stepper | price] (page-level add-to-cart)
  const layoutListActions = (input) => {
    const row = input.closest('.tileparent');
    const isQuicklist = !!row?.closest('.ex-quicklists');
    const isFrequent = !!row?.closest('.ex-frequentlypurchased');
    if (!isQuicklist && !isFrequent) return;
    const group = input.closest('.buttons.group');
    const qty = input.closest('.qnt-count');
    if (!group || !qty) return;

    const cart = group.querySelector('.btn-add-to-cart');
    const del = group.querySelector('.delete');
    const shopping = row.querySelector('.shopping-actions');

    if (cart) qty.after(cart);
    if (shopping && shopping.parentElement !== group) {
      (del || cart || qty).after(shopping);
    }

    group.classList.add(isFrequent ? 'fp-qty-actions' : 'ql-qty-actions');
  };

  const wrapQtyInput = (input) => {
    if (input.closest(`.${readyClass}`) || input.closest('.qnt-count') || !input.parentNode) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'qnt-count';
    const minus = makeBtn('-');
    const plus = makeBtn('+');
    input.parentNode.insertBefore(wrapper, input);
    wrapper.append(minus, input, plus);
    bindQtyControls(wrapper, input, minus, plus);
    layoutListActions(input);
  };

  // Quote already has +/- ; Vue re-renders them, so clicks are delegated
  const bindQuoteQty = (wrapper) => {
    const input = wrapper.querySelector('input');
    const buttons = wrapper.querySelectorAll('.incr-btn');
    if (!input || buttons.length < 2) return;
    bindQtyControls(wrapper, input, buttons[0], buttons[1], { clicks: false });
  };

  const stepQuoteQty = (btn) => {
    const wrapper = btn.closest('.qnt-count');
    const input = wrapper?.querySelector('input');
    if (!wrapper || !input) return;

    Object.assign(wrapper.dataset, parseQtyMeta(input));
    const buttons = [...wrapper.querySelectorAll('.incr-btn')];
    const dir = btn === buttons[0] || /[–−-]/.test(btn.textContent) ? -1 : 1;
    const min = getMin(wrapper);
    const next = nextQty(parseQty(input.value, min), getStep(wrapper), min, dir);

    input.value = formatQty(next);
    syncQuoteModel(input, next);
    syncMatchingInputs(input, next);

    const minus = buttons[0];
    if (minus) {
      const atMin = parseQty(next, min) <= min;
      minus.classList.toggle('disabled', atMin);
      minus.disabled = atMin;
    }
  };

  const enhanceQuoteQty = () => {
    document.querySelectorAll(quoteQtySelector).forEach(bindQuoteQty);
  };

  let inventoryReady = false;

  const rowInventoryReady = (el) => {
    const row = el.closest('.tile') || el.closest('.tileparent');
    return !!row?.querySelector('.live-inventory-finished-loading');
  };

  const syncMinusBtn = (wrapper, input) => {
    const minus = wrapper.querySelector('.incr-btn');
    if (!minus) return;
    const min = getMin(wrapper);
    const floor = allowsZeroQty(input) ? 0 : min;
    const atMin = parseQty(input.value, floor) <= floor;
    minus.classList.toggle('disabled', atMin);
    minus.disabled = atMin;
  };

  // Cache compare / PLP mins first, then apply them to already-wrapped rows
  const refreshQtyMeta = () => {
    const wrappers = [...document.querySelectorAll(qtyWrapSelector)];

    wrappers.forEach((wrapper) => {
      const input = wrapper.querySelector('input.inputquantity');
      if (!input) return;
      rememberMeta(input, parseQtyMeta(input));
    });

    wrappers.forEach((wrapper) => {
      const input = wrapper.querySelector('input.inputquantity');
      if (!input || !wrapper.classList.contains(readyClass)) return;

      const prevMin = getMin(wrapper);
      const meta = parseQtyMeta(input);
      Object.assign(wrapper.dataset, meta);
      rememberMeta(input, meta);

      const min = getMin(wrapper);
      const val = parseQty(input.value, 0);
      const silent = allowsZeroQty(input);

      // FP can stay at 0; only snap if still on the old catalog min
      if (!(silent && val <= 0) && (!val || (val === prevMin && val < min))) {
        input.value = formatQty(min);
        if (!silent) {
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncMinusBtn(wrapper, input);
    });
  };

  const enhanceQtyInputs = () => {
    document.querySelectorAll(qtySelector).forEach((input) => {
      if (!inventoryReady && !rowInventoryReady(input)) return;
      wrapQtyInput(input);
    });
    refreshQtyMeta();
  };

  const enhanceCompareQty = () => {
    document.querySelectorAll(compareQtySelector).forEach(wrapQtyInput);
    refreshQtyMeta();
    syncCompareFromPage();
  };

  const waitForInventory = () => {
    inventoryReady = false;
    enhanceQtyInputs();
    enhancePreRenderedFpWrappers();
  };

  const watchQuoteList = () => {
    const tbody = document.querySelector('#quote-product-list tbody');
    if (!tbody || tbody.dataset.qtyObserved) return;
    tbody.dataset.qtyObserved = '1';
    new MutationObserver(enhanceQuoteQty).observe(tbody, { childList: true, subtree: true });
  };

  try {
    document.addEventListener('product-loadmore-success', waitForInventory, false);
    document.addEventListener('search-success', waitForInventory, false);
    document.addEventListener('product-quickview-success', waitForInventory, false);
    document.addEventListener('live-pricing-done', () => {
      enhanceQtyInputs();
      enhancePreRenderedFpWrappers();
    }, false);
    document.addEventListener('live-inventory-done', () => {
      inventoryReady = true;
      enhanceQtyInputs();
      enhancePreRenderedFpWrappers();
    }, false);

    if (window.jQuery) {
      window.jQuery(document).ajaxSuccess((_event, xhr, settings) => {
        const url = settings?.url || '';
        if (!/pric|tims|erp/i.test(url)) return;
        let data = xhr.responseJSON;
        if (!data && xhr.responseText) {
          try {
            data = JSON.parse(xhr.responseText);
          } catch (_err) {
            return;
          }
        }
        if (!data) return;
        ingestErpPayload(data);
        refreshQtyMeta();
      });
    }

    enhanceCompareQty();
    enhanceQuoteQty();
    enhanceQtyInputs();
    enhancePreRenderedFpWrappers();

    // Capture phase so Vue re-renders cannot put ±1 back in front
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.incr-btn');
      if (!btn || !isQuoteQty(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      stepQuoteQty(btn);
    }, true);

    watchQuoteList();
    document.addEventListener('shown.bs.modal', () => {
      enhanceQuoteQty();
      enhanceCompareQty();
      watchQuoteList();
    }, false);

    const compareRoot = document.querySelector('#productCompare');
    if (compareRoot && !compareRoot.dataset.qtyObserved) {
      compareRoot.dataset.qtyObserved = '1';
      new MutationObserver(enhanceCompareQty)
        .observe(compareRoot, { childList: true, subtree: true });
    }

    document.addEventListener('click', (e) => {
      if (!e.target.closest('#quote_submit')) return;
      document.querySelectorAll('#quote-product-list .qnt-count input').forEach((input) => {
        const wrapper = input.closest('.qnt-count');
        if (!wrapper) return;
        const min = getMin(wrapper);
        const qty = Math.max(parseQty(input.value, min) || min, min);
        input.value = formatQty(qty);
        syncQuoteModel(input, qty);
      });
    }, true);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error: PLP qty buttons', error);
  }
})();
